import React, { useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { Box, Paper, Typography } from "@mui/material";
import { MonthlyBucket, CashflowAllocation } from "models/CashflowModel";
import { ChartStackingMode, SUPPLIER_COLORS, STATUS_COLORS } from "../types";
import { formatMonthLabel, formatCurrency } from "../utils";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface CashflowBarChartProps {
  months: Record<string, MonthlyBucket>;
  monthKeys: string[];
  chartStackingMode: ChartStackingMode;
  onMonthClick: (monthKey: string) => void;
}

export default function CashflowBarChart({
  months,
  monthKeys,
  chartStackingMode,
  onMonthClick,
}: CashflowBarChartProps) {
  const labels = monthKeys.map(formatMonthLabel);

  const datasets = useMemo(() => {
    if (chartStackingMode === "bySupplier") {
      // Get unique suppliers across all months
      const supplierSet = new Set<string>();
      for (const key of monthKeys) {
        const month = months[key];
        if (month) {
          for (const s of Object.keys(month.bySupplier)) {
            supplierSet.add(s);
          }
        }
      }
      const suppliers = Array.from(supplierSet).sort();

      return suppliers.map((supplier, idx) => ({
        label: supplier,
        data: monthKeys.map(key => {
          const month = months[key];
          return month?.bySupplier[supplier] || 0;
        }),
        backgroundColor: SUPPLIER_COLORS[idx % SUPPLIER_COLORS.length],
        borderRadius: 2,
      }));
    } else {
      // By status
      const statuses = ["placed", "forecasted"];
      return statuses.map(status => ({
        label: status.charAt(0).toUpperCase() + status.slice(1),
        data: monthKeys.map(key => {
          const month = months[key];
          if (!month) return 0;
          return month.entries
            .filter((e: CashflowAllocation) => e.status === status)
            .reduce((sum: number, e: CashflowAllocation) => sum + e.trancheAmount, 0);
        }),
        backgroundColor: STATUS_COLORS[status] || "#9E9E9E",
        borderRadius: 2,
      }));
    }
  }, [months, monthKeys, chartStackingMode]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index" as const,
      intersect: false,
    },
    onClick: (_event: any, elements: any[]) => {
      if (elements.length > 0) {
        const index = elements[0].index;
        onMonthClick(monthKeys[index]);
      }
    },
    plugins: {
      legend: {
        position: "top" as const,
        labels: {
          color: "#334155",
          font: { size: 12, weight: "500" as const },
        },
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            return `${context.dataset.label}: ${formatCurrency(context.parsed.y)}`;
          },
          footer: (items: any[]) => {
            const total = items.reduce((sum: number, item: any) => sum + item.parsed.y, 0);
            return `Total: ${formatCurrency(total)}`;
          },
        },
      },
      datalabels: {
        display: (context: any) => context.dataset.data[context.dataIndex] > 0,
        color: "#334155",
        font: { size: 11, weight: "bold" as const },
        anchor: "center" as const,
        align: "center" as const,
        formatter: (value: number) => formatCurrency(value),
      },
    },
    scales: {
      x: {
        stacked: true,
        ticks: {
          color: "#334155",
          font: { size: 12 },
        },
        grid: {
          display: false,
        },
      },
      y: {
        stacked: true,
        ticks: {
          color: "#334155",
          font: { size: 12 },
          callback: (value: any) => formatCurrency(value),
        },
        grid: {
          color: "#E2E8F0",
        },
      },
    },
  }), [monthKeys, onMonthClick]);

  const hasData = monthKeys.some(key => months[key]?.total > 0);

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Monthly Cash Outflows
      </Typography>
      {hasData ? (
        <Box sx={{ height: 350 }}>
          <Bar data={{ labels, datasets }} options={options} />
        </Box>
      ) : (
        <Box sx={{ height: 200, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography variant="body2" sx={{ opacity: 0.5 }}>
            No cashflow data for the selected period. Add POs with amounts and suppliers to see projections.
          </Typography>
        </Box>
      )}
    </Paper>
  );
}
